import { AdyenPaymentMethods } from '@appliedsystems/payments-core';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiClient } from 'src/api/ApiClient';
import { useCheckoutStore } from 'src/components/CheckoutV2/useCheckoutStore';
import { ErrorMessage } from 'src/components/ErrorAlert/ErrorAlert';
import { getSubdomain } from 'src/util/util';
import { useURLQuery } from './useURLQuery';

export function usePaymentMethod() {
  const subdomain = getSubdomain();
  const query = useURLQuery();
  const { pathname } = useLocation();
  const { agencyToken } = useCheckoutStore();

  const hppToken = useMemo(() => query.get('token') || agencyToken, [agencyToken, query]);

  const {
    data: response,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['usePaymentMethod', hppToken, subdomain],
    queryFn: () => {
      return ApiClient.getInstance().getPaymentMethodConfig(hppToken, subdomain);
    },
    staleTime: 1000 * 60 * 5,
    enabled: (!!hppToken || !!subdomain) && pathname.includes('v2'), // Do not call this on v1
  });

  const { errorMessage, data } = useMemo<{
    errorMessage?: ErrorMessage;
    data?: AdyenPaymentMethods[];
  }>(() => {
    if (error) {
      console.error('Error loading payment method', error);
      return { errorMessage: ['FETCH_PAYMENT_METHOD_FAILED_UNKNOWN'] };
    }
    if (!response) return {};

    if (response.status === 'ok') return { data: response.data };

    if (response.type === 'network' || response.status > 500)
      return { errorMessage: ['FETCH_PAYMENT_METHOD_FAILED_NETWORK'] };
    else if (response.status === 500)
      return {
        errorMessage: ['FETCH_PAYMENT_METHOD_FAILED_INTERNAL', { id: response.traceId }],
      };
    else
      return {
        errorMessage: ['FETCH_PAYMENT_METHOD_FAILED_UNKNOWN', { id: response.traceId }],
      };
  }, [response, error]);

  return {
    data,
    isLoading,
    errorMessage,
  };
}
